import {
  Server,
  ArrowRight,
  Terminal,
  Boxes,
  Globe,
  KeyRound,
  CheckCircle2,
  FileText,
  Shield,
  ShieldAlert,
  ShieldCheck,
  FileSearch,
} from 'lucide-react';
import { LandingHeader } from '@/components/LandingHeader';
import { LandingFooter } from '@/components/LandingFooter';
import { JsonLd } from '@/components/JsonLd';
import { buttonVariants } from '@/components/ui/Button';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { useT } from '@/i18n/provider';
import { SITE_LOCALE, SITE_ORIGIN, HREFLANG_TAGS } from '@/lib/site-config';
import { trackEvent } from '@/lib/analytics';
import { cn } from '@/lib/utils';

const steps = [
  { key: 'step1', icon: Terminal, accent: 'from-primary-600 to-primary-500' },
  { key: 'step2', icon: Boxes, accent: 'from-info-500 to-info-400' },
  { key: 'step3', icon: ArrowRight, accent: 'from-success-500 to-success-400' },
] as const;

type CategoryKey = 'authentication' | 'firewall' | 'dns' | 'audit';

const templates: {
  id: string;
  label: string;
  category: CategoryKey;
  dataModels: string[];
  icon: typeof KeyRound;
  iconColor: string;
  iconBg: string;
}[] = [
  {
    id: 'sshd',
    label: 'OpenSSH',
    category: 'authentication',
    dataModels: ['authentication'],
    icon: KeyRound,
    iconColor: 'text-info-500',
    iconBg: 'bg-info-50 dark:bg-info-500/10',
  },
  {
    id: 'auditd',
    label: 'Linux auditd',
    category: 'audit',
    dataModels: ['authentication', 'endpoint', 'security-event'],
    icon: FileSearch,
    iconColor: 'text-primary-500',
    iconBg: 'bg-primary-50 dark:bg-primary-500/10',
  },
  {
    id: 'fortinet',
    label: 'Fortinet FortiGate',
    category: 'firewall',
    dataModels: ['firewall', 'security-event'],
    icon: Shield,
    iconColor: 'text-success-500',
    iconBg: 'bg-success-50 dark:bg-success-500/10',
  },
  {
    id: 'paloalto',
    label: 'Palo Alto PAN-OS',
    category: 'firewall',
    dataModels: ['firewall', 'security-event'],
    icon: ShieldAlert,
    iconColor: 'text-warning-500',
    iconBg: 'bg-warning-50 dark:bg-warning-500/10',
  },
  {
    id: 'pfsense',
    label: 'pfSense / OPNsense',
    category: 'firewall',
    dataModels: ['firewall'],
    icon: ShieldCheck,
    iconColor: 'text-success-500',
    iconBg: 'bg-success-50 dark:bg-success-500/10',
  },
  {
    id: 'dnsmasq',
    label: 'dnsmasq',
    category: 'dns',
    dataModels: ['dns'],
    icon: Globe,
    iconColor: 'text-info-500',
    iconBg: 'bg-info-50 dark:bg-info-500/10',
  },
];

const benefits = [
  { key: 'b1', icon: Terminal, color: 'text-primary-500', bg: 'bg-primary-50 dark:bg-primary-500/10' },
  { key: 'b2', icon: Boxes, color: 'text-info-500', bg: 'bg-info-50 dark:bg-info-500/10' },
  { key: 'b3', icon: Globe, color: 'text-success-500', bg: 'bg-success-50 dark:bg-success-500/10' },
  { key: 'b4', icon: KeyRound, color: 'text-warning-500', bg: 'bg-warning-50 dark:bg-warning-500/10' },
] as const;

export function FleetPage() {
  const t = useT();

  useDocumentMeta({
    title: t('fleetPage.meta.title'),
    description: t('fleetPage.meta.description'),
  });

  const pageUrl = `${SITE_ORIGIN}/agents`;

  const webPageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: t('fleetPage.meta.title'),
    description: t('fleetPage.meta.description'),
    url: pageUrl,
    inLanguage: HREFLANG_TAGS[SITE_LOCALE],
    isPartOf: {
      '@type': 'WebSite',
      name: 'LogPulse',
      url: SITE_ORIGIN,
    },
    publisher: {
      '@type': 'Organization',
      name: 'LogPulse',
      url: SITE_ORIGIN,
    },
  };

  const howToJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: t('fleetPage.howItWorks.title'),
    description: t('fleetPage.intro.body'),
    inLanguage: HREFLANG_TAGS[SITE_LOCALE],
    step: steps.map((s, i) => ({
      '@type': 'HowToStep',
      position: i + 1,
      name: t(`fleetPage.howItWorks.${s.key}.title`),
      text: t(`fleetPage.howItWorks.${s.key}.body`),
    })),
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <JsonLd data={webPageJsonLd} />
      <JsonLd data={howToJsonLd} />
      <LandingHeader />

      {/* Hero */}
      <section className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 gradient-mesh overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 -right-20 w-96 h-96 rounded-full bg-primary-500/5 blur-3xl" />
          <div className="absolute bottom-1/4 -left-20 w-80 h-80 rounded-full bg-primary-400/5 blur-3xl" />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800/30 mb-8">
            <Server className="h-4 w-4 text-primary-500" />
            <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
              {t('fleetPage.hero.badge')}
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-balance">
            <span className="text-gradient">{t('fleetPage.hero.title')}</span>
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto text-balance">
            {t('fleetPage.hero.subtitle')}
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <a
              href="https://app.logpulse.io/sign-in"
              className={buttonVariants({ variant: 'gradient', size: '2xl' })}
              onClick={() => trackEvent('fleet_page_cta_click', { cta: 'primary' })}
            >
              {t('fleetPage.hero.cta')}
              <ArrowRight className="h-5 w-5 ml-1" />
            </a>
            <a
              href="/docs/integrations/vector-agent"
              className={buttonVariants({ variant: 'outline', size: '2xl' })}
              onClick={() => trackEvent('fleet_page_cta_click', { cta: 'docs' })}
            >
              <FileText className="h-4 w-4 mr-1.5" />
              {t('fleetPage.hero.ctaSecondary')}
            </a>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20 sm:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 dark:text-white mb-6 text-balance">
            {t('fleetPage.intro.title')}
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
            {t('fleetPage.intro.body')}
          </p>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 sm:py-24 gradient-subtle">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 dark:text-white mb-12 text-center text-balance">
            {t('fleetPage.howItWorks.title')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {steps.map((step) => {
              const Icon = step.icon;
              return (
                <div
                  key={step.key}
                  className="glass-subtle rounded-2xl p-6 sm:p-8 border border-gray-200/60 dark:border-gray-800/60"
                >
                  <div
                    className={cn(
                      'w-12 h-12 rounded-xl bg-gradient-to-br flex items-center justify-center mb-5 shadow-md',
                      step.accent,
                    )}
                  >
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                    {t(`fleetPage.howItWorks.${step.key}.title`)}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                    {t(`fleetPage.howItWorks.${step.key}.body`)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Built-in parser templates */}
      <section className="py-20 sm:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 dark:text-white mb-4 text-balance">
              {t('fleetPage.templates.title')}
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
              {t('fleetPage.templates.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {templates.map((tpl) => {
              const Icon = tpl.icon;
              return (
                <div
                  key={tpl.id}
                  className="glass-subtle rounded-2xl p-5 sm:p-6 border border-gray-200/60 dark:border-gray-800/60 flex flex-col"
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center shrink-0', tpl.iconBg)}>
                      <Icon className={cn('h-5 w-5', tpl.iconColor)} />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-gray-900 dark:text-white text-sm leading-tight">
                        {tpl.label}
                      </h3>
                      <p className="mt-0.5 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        {t(`fleetPage.templates.cat.${tpl.category}`)}
                      </p>
                    </div>
                  </div>

                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed flex-1">
                    {t(`fleetPage.templates.items.${tpl.id}.description`)}
                  </p>

                  <div className="mt-4 pt-4 border-t border-gray-200/50 dark:border-gray-700/40 flex flex-wrap gap-1.5">
                    {tpl.dataModels.map((dm) => (
                      <span
                        key={dm}
                        className="inline-flex items-center px-2 py-0.5 rounded-md bg-gray-100 dark:bg-gray-800/60 text-[11px] font-mono font-medium text-gray-700 dark:text-gray-300 border border-gray-200/60 dark:border-gray-700/40"
                      >
                        {dm}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          <p className="mt-10 text-center text-sm text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed">
            {t('fleetPage.templates.footnote')}
          </p>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 sm:py-24 gradient-subtle">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 dark:text-white mb-12 text-center text-balance">
            {t('fleetPage.benefits.title')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {benefits.map((b) => {
              const Icon = b.icon;
              return (
                <div
                  key={b.key}
                  className="flex items-start gap-4 p-6 rounded-2xl glass-subtle border border-gray-200/60 dark:border-gray-800/60"
                >
                  <div className={cn('w-12 h-12 rounded-xl flex items-center justify-center shrink-0', b.bg)}>
                    <Icon className={cn('h-6 w-6', b.color)} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                      {t(`fleetPage.benefits.${b.key}.title`)}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                      {t(`fleetPage.benefits.${b.key}.body`)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 sm:py-32">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 dark:text-white mb-4 text-balance">
            {t('fleetPage.cta.title')}
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-8 text-balance">
            {t('fleetPage.cta.body')}
          </p>
          <a
            href="https://app.logpulse.io/sign-in"
            className={buttonVariants({ variant: 'gradient', size: '2xl' })}
            onClick={() => trackEvent('fleet_page_cta_click', { cta: 'final' })}
          >
            {t('fleetPage.cta.button')}
            <ArrowRight className="h-5 w-5 ml-1" />
          </a>
          <div className="mt-6 flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-500">
            <CheckCircle2 className="h-4 w-4 text-success-500" />
            <span>100 MB/day · 7-day retention · No credit card</span>
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
}
